import React, { useState, useEffect } from "react";
import { Space, Input, Layout, message, Col } from "antd";
import { SearchOutlined, MoreOutlined } from "@ant-design/icons";
import TableWithLoadingWrapper from "../../../components/generic/dataGrid";
import EditGeneralLedgerForm from "./editGeneralLedgerForm";
import AddGeneralLedgerForm from "./addGeneralLedgerForm";
import TitledSection from "../../../components/generic/titledSection";
import { backendDeleteWithoutRefresh } from "../../../helpers/callBackend";
import GenericDropdown from "../../../components/generic/genericDropdown";
import HeaderBox from "../../../components/generic/arpariBoxes/headerBox";
import PermissionGate from "../../../helpers/PermissionGate";
import Button from '@mui/material/Button'


const generalLedgerDataFormat = {
	id: "",
	code: "",
	description: "",
	isDebit: "",
	accountType: "",
	reportType: "",
	totalIntoId: "",
};

const reportTypeMap = {
	BAL: "Balance Sheet",
	INC: "Income Statement",
};

const GeneralLedgerAccounts = (props) => {
	const [tableData, setTableData] = useState("");
	const [showData, setShowData] = useState("");
	const [loadingTable, setLoadingTable] = useState(true);
	const [recordsCount, setRecordsCount] = useState(-1);

	useEffect(() => {
		setRecordsCount(props.generalLedgerAccountRecordsCount + props.generalLedgerHeaderRecordsCount);
	}, [props.generalLedgerAccountRecordsCount, props.generalLedgerHeaderRecordsCount]);

	useEffect(() => {
		setLoadingTable(props.loadingGeneralLedgerAccount && props.loadingGeneralLedgerHeader);
	}, [props.loadingGeneralLedgerAccount, props.loadingGeneralLedgerHeader]);

	const addGeneralLedger = (type, newLedger) => {

		if (type === "Header") {
			props.setGeneralLedgerHeader((prevState) => {
				return [newLedger, ...prevState]
			})
		} else {
			props.setGeneralLedgerAccount((prevState) => {
				return [newLedger, ...prevState]
			})
		}
	}

	const editGeneralLedger = (record, editedLedger) => {

		if (record.accountType === "Heading") {
			props.setGeneralLedgerHeader((prevState) => {
				const newState = prevState.map((item) => {
					if (item.id === editedLedger.id) {
						return editedLedger
					}
					return item
				})
				return newState
			})
		} else {
			props.setGeneralLedgerAccount((prevState) => {
				const newState = prevState.map((item) => {
					if (item.id === editedLedger.id) {
						return editedLedger
					}
					return item
				})
				return newState
			})
		}
	}

	const deleteGeneralLedger = (record) => {

		if (record.accountType === "Heading") {
			props.setGeneralLedgerHeader((prevState) => {
				const newState = prevState.filter((item) => item.id !== record.id)
				return newState
			})
		} else {
			props.setGeneralLedgerAccount((prevState) => {
				const newState = prevState.filter((item) => item.id !== record.id)
				return newState
			})
		}
	}



	useEffect(() => {
		const ledgerData = [...props.generalLedgerHeader, ...props.generalLedgerAccount];
		const processedData = [];
		for (let i = 0; i < ledgerData.length; i++) {
			const item = ledgerData[i];
			const data = {
				id: item["id"],
				code: item["code"],
				description: item["description"],
				isDebit: item["is_debit"] === true || item["is_debit"] === false ? (item["is_debit"] ? "Debit" : "Credit") : "",
				accountType:
					item["account_type"] === "TOT" || item["account_type"] === "REG"
						? item["account_type"] === "TOT"
							? "Total"
							: "Regular"
						: "Heading",
				reportType: item["report_type"],
				totalIntoId: item["total_into_id"]
					? props.generalLedgerAccount[props.generalLedgerAccount.findIndex((key) => key.id === item["total_into_id"])]?.description || ""
					: "",
			};
			processedData.push(data);
		}
		setTableData(processedData);
		setShowData(processedData);
	}, [props.generalLedgerAccount, props.generalLedgerHeader]);

	const handleSearch = (e) => {
		if (e.target.value === "") {
			setShowData([...tableData]);
		} else {
			const search_text = e.target.value;
			const data = [...tableData];
			const resData = [];
			for (let i = 0; i < data.length; i++) {
				const prop_name1 = String(data[i]["code"]);
				const prop_name2 = data[i]["description"];
				const prop_name3 = data[i]["isDebit"];
				const prop_name4 = data[i]["accountType"];
				const prop_name5 = data[i]["reportType"];
				const prop_name6 = data[i]["totalIntoId"];
				if (
					prop_name1.toLowerCase().indexOf(search_text.toLowerCase()) > -1 ||
					prop_name2.toLowerCase().indexOf(search_text.toLowerCase()) > -1 ||
					prop_name3.toLowerCase().indexOf(search_text.toLowerCase()) > -1 ||
					prop_name4.toLowerCase().indexOf(search_text.toLowerCase()) > -1 ||
					prop_name5.toLowerCase().indexOf(search_text.toLowerCase()) > -1 ||
					prop_name6.toLowerCase().indexOf(search_text.toLowerCase()) > -1
				) {
					resData.push(data[i]);
				}
			}
			setShowData(resData);
		}
	};

	const handleDelete = async (record) => {
		const url = record.accountType === "Heading" ? "/api/general-ledger/header/" : "/api/general-ledger/account/";

		let isDeleteAllowed = true;

		if (record.accountType === "Heading") {
			isDeleteAllowed = true;
		} else {
			const children = props.generalLedgerAccount.filter((item) => item.total_into_id === record.id);
			if (children.length > 0) {
				isDeleteAllowed = false;
			}
		}

		if (!isDeleteAllowed) {
			message.error("Cannot delete the customer ledger. Please delete all the ledger accounts associated with this ledger first");
			return;
		}

		try {
			const res = await backendDeleteWithoutRefresh(url, {
				id: record.id,
			});

			if (res && res.status === 200) {
				deleteGeneralLedger(record);
			}
		} catch (err) {
			console.error(err);
		}
	};

	const totalIntoData = props.generalLedgerAccount.map((item) => {
		return {
			text: item.description,
			value: item.description,
		};
	});

	const columns = [
		{
			field: 'code',
			headerName: 'Code',
			valueGetter: (params) => {
				return params;
			},
			sortComparator: (v1, v2) => {
				return v1 <= v2;
			},
			flex: 1,
		},
		{
			field: 'description',
			headerName: 'Description',
			sortComparator: (v1, v2) => v1.localeCompare(v2),
			flex: 1.5,
		},
		{
			field: 'isDebit',
			headerName: 'Normal Balance',
			sortComparator: (v1, v2) => v1.localeCompare(v2),
			filterable: true,
			valueGetter: (params) => params,
			renderCell: (params) => params.value,
			flex: 1,
		},
		{
			field: 'accountType',
			headerName: 'Account Type',
			sortComparator: (v1, v2) => v1.localeCompare(v2),
			filterable: true,
			renderCell: (params) => params.value,
			flex: 1,
		},
		{
			field: 'reportType',
			headerName: 'Report Type',
			valueGetter: (params) => reportTypeMap[params],
			sortComparator: (v1, v2) => v1.localeCompare(v2),
			flex: 1,
		},
		{
			field: 'totalIntoId',
			headerName: 'Total Into',
			sortComparator: (v1, v2) => v1.localeCompare(v2),
			filterable: true,
			renderCell: (params) => params.value,
			flex: 1.5,
		},
		{
			field: 'actions',
			headerName: '',
            flex: 0.1,
            align: 'right',
			renderCell: (params) => (
        <PermissionGate requiredRole='dashboardEditor'>
				  <GenericDropdown
				  	dropdownItems={[
				  		<EditGeneralLedgerForm
				  			ledger={params.row}
				  			generalLedgerAccount={props.generalLedgerAccount}
				  			generalLedger={tableData}
				  			editGeneralLedger={editGeneralLedger}
				  		/>,
				  		<a
				  			style={{ color: 'red', cursor: 'pointer' }}
				  			onClick={() => { handleDelete(params.row); }}
				  		>
				  			Delete
				  		</a>,
				  	]}
				  />
        </PermissionGate>
			),
		},
	];

	return (
		<>
		<HeaderBox
			title={"General Ledger Accounts"}
			ignoreDocumentTitle={true}
			actions={
          <PermissionGate
            toolTipText='Only admins and dashboard editors may add new general ledger accounts'
            requiredRole='dashboardEditor'
            readOnly={
              <Button 
                sx={{ fontSize: 14, textTransform: 'none' }}
                fullWidth
                size="small"
                variant="contained"
                disabled
              >
                + Add New
              </Button>
            }
          >
				<AddGeneralLedgerForm
					generalLedgerAccount={props.generalLedgerAccount}
					generalLedger={tableData}
					addGeneralLedger={addGeneralLedger}
				/>
        </PermissionGate>
			}
		/>
		<Space direction="vertical" style={{ width: "100%", marginTop: 6 }}>
			<Col span={16}>
				<Input
					disabled={recordsCount < 0 || recordsCount * 0.9 > props.generalLedgerAccount.length + props.generalLedgerHeader.length}
					placeholder="Search"
					prefix={<SearchOutlined />}
					onChange={handleSearch}
				/>
			</Col>
			<TableWithLoadingWrapper
				pagination={{
					paginationModel: { pageSize: showData?.length && showData.length <= 10 ? showData.length : 10, page: 0 },
				}}
				sortModel={[
					{
						field: "code",
						sort: "asc",
					},
				]}
				pageSizeOptions={[5, 10, 25]}
				columns={columns}
				dataSource={[...showData]}
				loading={loadingTable}
				dataFormat={generalLedgerDataFormat}
			/>
		</Space>
		</>
	);
};

export default GeneralLedgerAccounts;
